import React, { useMemo } from 'react';
import Piechart from '../Charts/Piechart';
import Barchart from '../Charts/Barchart';

// ── Analysis Summary Card (matches code quality report aesthetic) ──────────────

function AnalysisSummaryCard({ value, label, sublabel, accentColor, iconBg, iconColor, icon }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8ecf0',
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Colored top accent line */}
      <div style={{ height: 4, background: accentColor }} />
      <div style={{
        flex: 1,
        padding: '20px 22px',
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        {/* Icon circle */}
        <div style={{
          width: 50, height: 50, borderRadius: '50%',
          background: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 20, color: iconColor }}>{icon}</span>
        </div>
        <div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 6, fontWeight: 600 }}>
            {label}
          </div>
          {sublabel && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontWeight: 500 }}>
              {sublabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalysisSummaryGrid({ rulesBreached, boundariesCrossed, complianceRate, edgesAnalyzed }) {
  const comp = complianceRate >= 95
    ? { accent: '#75C4C0', bg: '#e9f7f6', icon: '#0f766e' }
    : complianceRate >= 80
      ? { accent: '#f59e0b', bg: '#fffbeb', icon: '#f59e0b' }
      : { accent: '#EA7775', bg: '#fdeceb', icon: '#EA7775' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, height: '100%' }}>
      <AnalysisSummaryCard
        value={rulesBreached}
        label="Rules breached"
        sublabel="distinct rules broken"
        accentColor={rulesBreached > 0 ? '#EA7775' : '#94a3b8'}
        iconBg={rulesBreached > 0 ? '#fdeceb' : '#f8fafc'}
        iconColor={rulesBreached > 0 ? '#EA7775' : '#94a3b8'}
        icon="§"
      />
      <AnalysisSummaryCard
        value={boundariesCrossed}
        label="Boundaries crossed"
        sublabel="compliance rule seams violated"
        accentColor={boundariesCrossed > 0 ? '#EA7775' : '#94a3b8'}
        iconBg={boundariesCrossed > 0 ? '#fdeceb' : '#f8fafc'}
        iconColor={boundariesCrossed > 0 ? '#EA7775' : '#94a3b8'}
        icon="⇆"
      />
      <AnalysisSummaryCard
        value={`${complianceRate.toFixed(1)}%`}
        label="Compliance rate"
        sublabel="clean of analyzed edges"
        accentColor={comp.accent}
        iconBg={comp.bg}
        iconColor={comp.icon}
        icon="✓"
      />
      <AnalysisSummaryCard
        value={edgesAnalyzed}
        label="Edges analyzed"
        sublabel="dependency edges scanned"
        accentColor="#0ea5e9"
        iconBg="#eff6ff"
        iconColor="#0369a1"
        icon="⇄"
      />
    </div>
  );
}

// ── Status / Context Header ───────────────────────────────────────────────────

function StatusHeaderCard({ result, complianceRate }) {
  const {
    passed = false,
    totalViolations = 0,
    totalWarnings = 0,
    validatedAt,
    commitId,
    validatedById,
  } = result;

  // Guard against an unparseable validatedAt (mirrors ValidationResultPanel).
  const _date = validatedAt ? new Date(validatedAt) : null;
  const formattedAt = _date && !isNaN(_date.getTime())
    ? _date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;
  const isAutoRun = validatedById === 'ci';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      padding: '18px 24px',
      background: '#fff',
      border: '1px solid #e8ecf0',
      borderLeft: `5px solid ${passed ? '#75C4C0' : '#EA7775'}`,
      borderRadius: 12,
    }}>
      {/* Pass / fail verdict */}
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 14, fontWeight: 800, letterSpacing: '0.02em',
        padding: '7px 16px', borderRadius: 24,
        background: passed ? '#e9f7f6' : '#fdeceb',
        color: passed ? '#0f766e' : '#c0413e',
        border: `1px solid ${passed ? '#bfe6e3' : '#f3b5b3'}`,
      }}>
        {passed ? '✓ PASSED' : '✕ FAILED'}
      </span>

      {/* Headline counts */}
      <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: '#475569', fontWeight: 500 }}>
        <strong style={{ color: '#1e293b', fontWeight: 700 }}>{totalViolations.toLocaleString()}</strong> violation{totalViolations !== 1 ? 's' : ''}
        {' · '}
        <strong style={{ color: '#1e293b', fontWeight: 700 }}>{totalWarnings.toLocaleString()}</strong> warning{totalWarnings !== 1 ? 's' : ''}
        {' · '}
        <strong style={{ color: '#1e293b', fontWeight: 700 }}>{complianceRate.toFixed(1)}%</strong> compliant
      </div>

      {/* Provenance: commit · date · trigger */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {commitId && (
          <span style={{
            fontFamily: 'monospace', fontSize: 11, color: '#475569',
            background: '#f1f5f9', borderRadius: 4, padding: '2px 7px',
          }}>
            {commitId.substring(0, 8)}
          </span>
        )}
        {formattedAt && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{formattedAt}</span>
        )}
        {isAutoRun && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, color: '#0369a1',
            background: '#e0f2fe', borderRadius: 20, padding: '3px 10px',
          }}>
            ⚡ Auto-run on push
          </span>
        )}
      </div>
    </div>
  );
}

// ── Violation Density Card ────────────────────────────────────────────────────

function ViolationDensityCard({ totalViolations, totalEdgesAnalyzed }) {
  if (totalEdgesAnalyzed === 0) return null;

  const density = (totalViolations / totalEdgesAnalyzed * 1000).toFixed(1);
  const oneInN = totalViolations > 0 ? Math.round(totalEdgesAnalyzed / totalViolations) : null;

  const { accentColor, bg, textColor } = density >= 20
    ? { accentColor: '#EA7775', bg: '#fdeceb', textColor: '#c0413e' }
    : density >= 5
      ? { accentColor: '#f59e0b', bg: '#fffbeb', textColor: '#92400e' }
      : { accentColor: '#75C4C0', bg: '#e9f7f6', textColor: '#0f766e' };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 24,
      padding: '18px 24px',
      background: bg,
      border: `1px solid ${accentColor}40`,
      borderLeft: `5px solid ${accentColor}`,
      borderRadius: 12,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
        background: `${accentColor}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, color: accentColor,
      }}>
        ◈
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
          Violation Density
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: textColor, lineHeight: 1 }}>{density}</span>
          <span style={{ fontSize: 13, color: '#64748b' }}>violations per 1,000 edges</span>
        </div>
        {oneInN && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
            1 in every <strong style={{ color: textColor }}>{oneInN}</strong> dependency edges breaks an architecture rule
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>out of</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{totalEdgesAnalyzed.toLocaleString()}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>edges scanned</div>
      </div>
    </div>
  );
}

// ── Data transforms ───────────────────────────────────────────────────────────

function buildViolationTypeData(result) {
  return [
    { type: 'Violations', count: result.totalViolations, color: '#EA7775' },
    { type: 'Warnings', count: result.totalWarnings, color: '#f59e0b' },
    { type: 'Info', count: result.totalInfos, color: '#0ea5e9' },
  ].filter(d => d.count > 0);
}

function buildTopLayerPairs(violations, n = 5) {
  const counts = {};
  violations.forEach(v => {
    const key = `${v.sourceLayerName} → ${v.targetLayerName}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([smell, count]) => ({ smell, count }));
}

function buildTopTargetLayers(violations, n = 5) {
  const counts = {};
  violations.forEach(v => {
    counts[v.targetLayerName] = (counts[v.targetLayerName] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([smell, count]) => ({ smell, count }));
}

function buildRuleBreakdown(violations) {
  const map = {};
  violations.forEach(v => {
    const key = v.violatedRuleId || `${v.sourceLayerName}→${v.targetLayerName}`;
    if (!map[key]) {
      map[key] = {
        sourceLayer: v.sourceLayerName,
        targetLayer: v.targetLayerName,
        ruleId: v.violatedRuleId || null,
        enforcement: v.ruleEnforcement,
        count: 0,
      };
    }
    map[key].count += 1;
  });
  return Object.values(map).sort((a, b) => b.count - a.count);
}

function buildTopSourceLayers(violations, n = 5) {
  const counts = {};
  violations.forEach(v => {
    counts[v.sourceLayerName] = (counts[v.sourceLayerName] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([smell, count]) => ({ smell, count }));
}

function buildTopClasses(violations, n = 5) {
  const counts = {};
  violations.forEach(v => {
    counts[v.sourceNodeLabel] = (counts[v.sourceNodeLabel] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([smell, count]) => ({ smell, count }));
}

function buildTopDependencies(violations, n = 5) {
  const counts = {};
  violations.forEach(v => {
    const key = `${v.sourceNodeLabel} → ${v.targetNodeLabel}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([smell, count]) => ({ smell, count }));
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ArchValidationReport({ result }) {
  const violations = useMemo(
    () => (result?.violations ?? []).filter(v => v && typeof v === 'object'),
    [result]
  );

  const violationTypeData = useMemo(() => buildViolationTypeData(result), [result]);
  const topLayerPairs = useMemo(() => buildTopLayerPairs(violations), [violations]);
  const topClasses = useMemo(() => buildTopClasses(violations), [violations]);
  const topDependencies = useMemo(() => buildTopDependencies(violations), [violations]);
  const topTargetLayers = useMemo(() => buildTopTargetLayers(violations), [violations]);
  const topSourceLayers = useMemo(() => buildTopSourceLayers(violations), [violations]);
  const ruleBreakdown = useMemo(() => buildRuleBreakdown(violations), [violations]);
  const boundariesCrossed = useMemo(() => {
    const set = new Set();
    violations.forEach(v => set.add(`${v.sourceLayerName} → ${v.targetLayerName}`));
    return set.size;
  }, [violations]);
  const complianceRate = useMemo(() => {
    if (!result.totalEdgesAnalyzed) return 100;
    const clean = result.totalEdgesAnalyzed - result.totalViolations;
    return Math.max(0, (clean / result.totalEdgesAnalyzed) * 100);
  }, [result]);
  const totalForPct = result.totalViolations + result.totalWarnings + result.totalInfos;

  const hasViolations = violations.length > 0;

  return (
    <div style={{ marginTop: 28 }}>
      <div className="row g-4">

        {/* Status / context header — pass/fail verdict + provenance */}
        <div className="col-12">
          <StatusHeaderCard result={result} complianceRate={complianceRate} />
        </div>

        {/* Violation density */}
        {violations.length > 0 && (
          <div className="col-12">
            <ViolationDensityCard
              totalViolations={result.totalViolations}
              totalEdgesAnalyzed={result.totalEdgesAnalyzed}
            />
          </div>
        )}

        {/* Donut + Analysis summary */}
        {violationTypeData.length > 0 ? (
          <>
            <div className="col-lg-7" style={{ minHeight: 440 }}>
              <Piechart
                data={violationTypeData}
                title="Distribution of violation types"
              />
            </div>

            <div className="col-lg-5" style={{ minHeight: 440 }}>
              <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl h-100">
                <h5 className="mb-4 text-lg font-semibold text-gray-800">Analysis summary</h5>
                <AnalysisSummaryGrid
                  rulesBreached={ruleBreakdown.length}
                  boundariesCrossed={boundariesCrossed}
                  complianceRate={complianceRate}
                  edgesAnalyzed={result.totalEdgesAnalyzed}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="col-12">
            <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl text-center">
              <div style={{ fontSize: 36, marginBottom: 10, color: '#75C4C0' }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f766e', marginBottom: 4 }}>
                Architecture is fully compliant
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                No violations found across {result.totalEdgesAnalyzed.toLocaleString()} dependency edges analyzed.
              </div>
            </div>
          </div>
        )}

        {/* Unmapped nodes notice — shown below the donut + analysis summary */}
        {result.unmappedNodeCount > 0 && (
          <div className="col-12">
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 16,
              padding: '20px 24px',
              background: '#faf5ff',
              border: '1px solid #c4b5fd',
              borderLeft: '5px solid #7c3aed',
              borderRadius: 12,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: '#ede9fe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 20, color: '#7c3aed' }}>◎</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#4c1d95', marginBottom: 4 }}>
                  {result.unmappedNodeCount.toLocaleString()} node{result.unmappedNodeCount !== 1 ? 's' : ''} not assigned to any component
                </div>
                <div style={{ fontSize: 13, color: '#6d28d9', lineHeight: 1.6 }}>
                  These classes exist in the dependency graph but are <strong>invisible to all architecture rules</strong> — violations involving them are silently skipped.
                  Assign them to a component in the canvas to get full coverage.
                </div>
              </div>
              <div style={{
                flexShrink: 0, alignSelf: 'center',
                background: '#7c3aed', color: '#fff',
                borderRadius: 20, padding: '4px 14px',
                fontSize: 12, fontWeight: 700,
              }}>
                {result.unmappedNodeCount} unmapped
              </div>
            </div>
          </div>
        )}

        {/* Top violated layer boundaries */}
        {topLayerPairs.length > 0 && (
          <div className="col-12">
            <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl">
              <h5 className="mb-4 text-lg font-semibold text-gray-800">
                Top violated component boundaries
              </h5>
              <Barchart
                data={topLayerPairs}
                color="#EA7775"
                fadeColor="#F38181"
                gradientID="archLayerBoundaryGradient"
              />
            </div>
          </div>
        )}

        {/* Most violated target layer + most problematic source layer */}
        {(topTargetLayers.length > 0 || topSourceLayers.length > 0) && (
          <>
            {topTargetLayers.length > 0 && (
              <div className="col-md-6">
                <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl h-100">
                  <h5 className="mb-4 text-lg font-semibold text-gray-800">
                    Most violated target component
                  </h5>
                  <Barchart
                    data={topTargetLayers}
                    color="#EA7775"
                    fadeColor="#F38181"
                    gradientID="archTargetLayerGradient"
                  />
                </div>
              </div>
            )}
            {topSourceLayers.length > 0 && (
              <div className="col-md-6">
                <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl h-100">
                  <h5 className="mb-4 text-lg font-semibold text-gray-800">
                    Most problematic source component
                  </h5>
                  <Barchart
                    data={topSourceLayers}
                    color="#EA7775"
                    fadeColor="#F38181"
                    gradientID="archSourceLayerGradient"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Top offending source classes */}
        {topClasses.length > 0 && (
          <div className="col-12">
            <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl">
              <h5 className="mb-4 text-lg font-semibold text-gray-800">
                Top offending classes
              </h5>
              <Barchart
                data={topClasses}
                color="#EA7775"
                fadeColor="#F38181"
                gradientID="archOffendingClassGradient"
              />
            </div>
          </div>
        )}

        {/* Top forbidden dependencies — the specific source → target edges */}
        {topDependencies.length > 0 && (
          <div className="col-12">
            <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl">
              <h5 className="mb-4 text-lg font-semibold text-gray-800">
                Top forbidden dependencies
              </h5>
              <Barchart
                data={topDependencies}
                color="#EA7775"
                fadeColor="#F38181"
                gradientID="archForbiddenDependencyGradient"
              />
            </div>
          </div>
        )}

        {/* Rule breakdown table */}
        {ruleBreakdown.length > 0 && (
          <div className="col-12">
            <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl">
              <h5 className="mb-4 text-lg font-semibold text-gray-800">Rule breakdown</h5>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      {['#', 'Rule', 'Enforcement', 'Count', '% of total'].map(h => (
                        <th key={h} style={{
                          padding: '8px 12px',
                          textAlign: h === '#' || h === 'Count' || h === '% of total' ? 'center' : 'left',
                          fontSize: 11, fontWeight: 700, color: '#64748b',
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ruleBreakdown.map((row, i) => {
                      const CHIP = {
                        denied: { bg: '#fdeceb', color: '#c0413e', label: 'Violation' },
                        warning: { bg: '#fffbeb', color: '#92400e', label: 'Warning' },
                        info: { bg: '#eff6ff', color: '#0ea5e9', label: 'Info' },
                      };
                      const chip = CHIP[row.enforcement] ?? CHIP.denied;
                      const pct = totalForPct > 0 ? ((row.count / totalForPct) * 100).toFixed(1) : '0.0';
                      const barW = totalForPct > 0 ? (row.count / totalForPct) * 100 : 0;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#94a3b8', fontWeight: 600, verticalAlign: 'top' }}>
                            {i + 1}
                          </td>
                          <td style={{ padding: '10px 12px', maxWidth: 320, verticalAlign: 'top' }}>
                            {/* Layer boundary — primary info */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{
                                fontSize: 13, fontWeight: 700, color: '#1e293b',
                                background: '#f8fafc', border: '1px solid #e2e8f0',
                                borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap',
                              }}>
                                {row.sourceLayer}
                              </span>
                              <span style={{ color: chip.color, fontWeight: 700, fontSize: 14 }}>→</span>
                              <span style={{
                                fontSize: 13, fontWeight: 700, color: '#1e293b',
                                background: '#f8fafc', border: '1px solid #e2e8f0',
                                borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap',
                              }}>
                                {row.targetLayer}
                              </span>
                            </div>
                            {/* Rule ID — secondary reference */}
                            {row.ruleId && (
                              <div style={{ marginTop: 5 }}>
                                <span style={{
                                  fontSize: 10, fontFamily: 'monospace', color: '#94a3b8',
                                  background: '#f1f5f9', borderRadius: 4, padding: '1px 6px',
                                }}>
                                  {row.ruleId}
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', verticalAlign: 'top' }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
                              background: chip.bg, color: chip.color,
                            }}>
                              {chip.label}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#1e293b', verticalAlign: 'top' }}>
                            {row.count}
                          </td>
                          <td style={{ padding: '10px 12px', minWidth: 140, verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${barW}%`, height: '100%', background: chip.color, borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
